import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, FileCheck2, Loader2, AlertTriangle,
  ShieldCheck, Sparkles, History
} from "lucide-react";
import BackButton from "@/components/business/BackButton";
import PageHeader from "@/components/business/PageHeader";
import SectionHeader from "@/components/business/SectionHeader";
import { inr } from "@/lib/format";
import ExtractionCard from "@/components/business/ExtractionCard";
import { riskClasses } from "@/components/business/RiskBadge";

const DOC_TYPES = [
  { key: "ais", label: "AIS (Annual Information Statement)" },
  { key: "26as", label: "Form 26AS" },
  { key: "tis", label: "TIS (Taxpayer Information Summary)" },
  { key: "form16", label: "Form 16 (Salary certificate)" },
  { key: "form16a", label: "Form 16A (Non-salary TDS)" },
  { key: "bank_statement", label: "Bank statement" },
  { key: "capital_gains_statement", label: "Capital gains statement" },
];


export default function AISIngestion() {
  const [params] = useSearchParams();
  const initialFilingId = params.get("filing_id") || "";

  const [filings, setFilings] = useState([]);
  const [filingId, setFilingId] = useState(initialFilingId);
  const [docType, setDocType] = useState("ais");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [cross, setCross] = useState(null);

  useEffect(() => { api.get("/filings").then(({ data }) => setFilings(data)).catch(() => {}); }, []);

  const load = async () => {
    if (!filingId) return;
    const [{ data: q }, { data: h }, { data: x }] = await Promise.all([
      api.get("/ingestion/review-queue", { params: { filing_id: filingId } }),
      api.get(`/ingestion/extractions/${filingId}`),
      api.get(`/ingestion/cross-document/${filingId}`),
    ]);
    setQueue(q); setHistory(h); setCross(x);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filingId]);

  const upload = async () => {
    if (!file) return toast.error("Choose a file");
    if (!filingId) return toast.error("Select a filing");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("filing_id", filingId);
    setBusy(true);
    try {
      await api.post("/ingestion/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      toast.success("Uploaded — extraction in review queue");
      setFile(null); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <BackButton to="/practice" label="Practice" />
        <PageHeader>
          <PageHeader.Title>AIS &amp; 26AS Ingestion</PageHeader.Title>
          <PageHeader.Actions>
            <Badge variant="outline"><Sparkles className="h-3 w-3 mr-1" />AI-assisted</Badge>
          </PageHeader.Actions>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
          Upload the taxpayer's AIS, TIS, Form 26AS, Form 16 or bank statements. Our engine extracts every entry with a per-field confidence score, cross-checks against other uploaded documents, and queues the result for your review. <span className="font-semibold">Nothing prefills the return until you explicitly approve it.</span>
        </p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-5">
            <Card className="p-6 border-border">
              <div className="font-display font-semibold text-lg flex items-center gap-2"><Upload className="h-4 w-4 text-primary" />Upload document</div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Filing</Label>
                  <Select value={filingId} onValueChange={setFilingId}>
                    <SelectTrigger className="h-10 mt-1.5" data-testid="ais-filing-select"><SelectValue placeholder="Choose filing…" /></SelectTrigger>
                    <SelectContent>
                      {filings.map(f => <SelectItem key={f.id} value={f.id}>{f.itr_type} · AY {f.assessment_year} · {f.id.slice(0, 6)}</SelectItem>)}
                      {filings.length === 0 && <SelectItem value="none" disabled>No filings available</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Document type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="h-10 mt-1.5" data-testid="ais-doctype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map(d => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs">File (PDF or JSON, up to 25 MB)</Label>
                <Input ref={fileRef} type="file" accept=".pdf,.json,application/pdf,application/json" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-10 mt-1.5" data-testid="ais-file" />
              </div>
              <Button onClick={upload} disabled={busy || !file || !filingId} className="mt-4" data-testid="ais-upload-btn">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Extracting…</> : <><Upload className="h-4 w-4 mr-2" />Upload &amp; extract</>}
              </Button>
              <div className="mt-3 rounded bg-muted/40 border border-border p-3 text-xs text-muted-foreground">
                Never overwrites: every re-upload is stored as a new version. Original files, extracted JSON, AI model &amp; prompt version are retained for audit.
              </div>
            </Card>

            <Card className="p-6 border-border">
              <div className="font-display font-semibold text-lg flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Review queue</div>
              <p className="text-xs text-muted-foreground mt-1">Approve field-by-field. Only approved fields prefill the ITR.</p>
              <div className="mt-4 space-y-4">
                {queue.length === 0 && <div className="text-sm text-muted-foreground rounded border border-dashed border-border p-6 text-center">No pending extractions.</div>}
                {queue.map(e => <ExtractionCard key={e.id} extraction={e} onDone={load} />)}
              </div>
            </Card>
          </div>

          <div className="space-y-5">
            <Card className="p-5 border-border">
              <SectionHeader><AlertTriangle className="h-3.5 w-3.5" />Cross-document check</SectionHeader>
              {!cross || cross.documents_compared === 0 ? (
                <div className="mt-3 text-xs text-muted-foreground">Upload at least two documents to compare.</div>
              ) : cross.conflicts.length === 0 ? (
                <div className="mt-3 text-xs rounded border border-emerald-300 bg-emerald-50 p-3 text-emerald-800">All {cross.documents_compared} documents agree within tolerance.</div>
              ) : (
                <ul className="mt-3 space-y-2 text-xs">
                  {cross.conflicts.map(c => (
                    <li key={c.field_key} className={`rounded border p-2 ${riskClasses(c.severity)}`}>
                      <div className="font-semibold uppercase tracking-wide text-[10px]">{c.field_key}</div>
                      <div className="mt-1 font-mono">
                        min ₹{Math.round(c.min).toLocaleString()} · max ₹{Math.round(c.max).toLocaleString()} · Δ ₹{Math.round(c.diff).toLocaleString()}
                      </div>
                      <div className="mt-1">
                        {c.entries.map((x, i) => <div key={i}>{x.source}: ₹{Math.round(x.value).toLocaleString()}</div>)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-5 border-border">
              <SectionHeader><History className="h-3.5 w-3.5" />Extraction history</SectionHeader>
              <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                {history.length === 0 && <div className="text-xs text-muted-foreground">No history yet.</div>}
                {history.map(h => (
                  <div key={h.id} className="rounded border border-border p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold uppercase">{h.doc_type} v{h.version}</span>
                      <Badge variant="outline" className="text-[10px]">{h.review_status}</Badge>
                    </div>
                    <div className="mt-0.5 text-muted-foreground">
                      {h.ai_provider}/{h.ai_model} · {new Date(h.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

